import { useGetBugCountsQuery } from '../hooks/bug-management/useProjectFindings'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { X, Copy, Check, ExternalLink, Globe, Gift, ChevronDown } from 'lucide-react'
import type { Project, Bug } from '../data/projectTypes'
import { useGetProjectBugsQuery,  useUpdateProjectMutation } from '../services/projectDataApi'
import { buildShareUrl } from '../lib/shareUrl'
import { trackSegmentEvent } from '../lib/segment'
import ModalPortal from './ModalPortal'
import Spinner from './Spinner'
import './ShareDialog.css'

interface ShareDialogProps {
  project: Project
  onClose: () => void
  /** When true, the viewer can't edit the project, so "Make public" is shown as an explanatory note. */
  readOnly?: boolean
  /** Render the share UI inline (in the app's left panel, like Project Settings) instead of as a
   *  centered modal — no overlay, no close button, no Escape-to-close. */
  inline?: boolean
}

const SEVERITY_EMOJI: Record<string, string> = {
  critical: '🔴',
  high: '🟠',
  medium: '🟡',
  low: '🔵',
}
const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s
}

interface ShareData {
  projectName: string
  url: string
  total: number
  topBugs: { title: string; severity: string }[]
}

interface PlatformContent {
  text: string
  intentUrl?: string
  intentLabel?: string
  charLimit?: number
}

// Human-readable summary — the default "well formatted" view of the QA results.
function buildOverview(d: ShareData): PlatformContent {
  const lines: string[] = []
  lines.push(`QA Results — ${d.projectName}`)
  lines.push('')
  lines.push(d.total > 0
    ? `${d.total} ${d.total === 1 ? 'bug' : 'bugs'} found by Replay QA.`
    : 'No bugs found yet — looking clean so far. 🎉')
  if (d.topBugs.length > 0) {
    lines.push('')
    lines.push('Top issues identified:')
    for (const b of d.topBugs) {
      lines.push(`${SEVERITY_EMOJI[b.severity] ?? '•'} ${cap(b.severity)} — ${b.title}`)
    }
  }
  lines.push('')
  lines.push(`Full report: ${d.url}`)
  return { text: lines.join('\n') }
}

// X / Twitter — punchy, hashtagged, kept within the 280-char limit.
function buildX(d: ShareData): PlatformContent {
  const charLimit = 280
  const header = `🐛 ${d.total} ${d.total === 1 ? 'bug' : 'bugs'} found in ${d.projectName} with Replay QA`
  const footer = `\n\nFull report 👇\n${d.url}\n\n#QA #softwaretesting`
  let body = ''
  for (const b of d.topBugs) {
    const line = `\n${SEVERITY_EMOJI[b.severity] ?? '•'} ${truncate(b.title, 70)}`
    if ((header + body + line + footer).length > charLimit) break
    body += line
  }
  const text = header + body + footer
  return {
    text,
    intentUrl: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
    intentLabel: 'Open in X',
    charLimit,
  }
}

// LinkedIn — professional tone, longer form. LinkedIn's share dialog only accepts a URL, so the
// "Open" action seeds the link and the copied text is pasted by the user.
function buildLinkedIn(d: ShareData): PlatformContent {
  const lines: string[] = []
  lines.push(`We ran an automated QA pass on ${d.projectName} with Replay QA.`)
  lines.push('')
  lines.push(d.total > 0
    ? `The results: ${d.total} ${d.total === 1 ? 'issue' : 'issues'} surfaced. A few of the most important:`
    : 'The results: no issues surfaced so far — a clean run. 🎉')
  if (d.topBugs.length > 0) {
    lines.push('')
    for (const b of d.topBugs) {
      lines.push(`• ${cap(b.severity)}: ${b.title}`)
    }
  }
  lines.push('')
  lines.push('Explore the full interactive report here:')
  lines.push(d.url)
  lines.push('')
  lines.push('#QualityAssurance #SoftwareTesting #QA #ReplayQA')
  return {
    text: lines.join('\n'),
    intentUrl: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(d.url)}`,
    intentLabel: 'Open in LinkedIn',
  }
}

// Reddit — markdown-formatted self-post (title + body).
function buildReddit(d: ShareData): PlatformContent {
  const title = `QA results for ${d.projectName}: ${d.total} ${d.total === 1 ? 'bug' : 'bugs'} found with Replay QA`
  const body: string[] = []
  body.push(`I ran an automated QA pass on **${d.projectName}** using Replay QA and it surfaced **${d.total} ${d.total === 1 ? 'bug' : 'bugs'}**.`)
  if (d.topBugs.length > 0) {
    body.push('')
    body.push('**Top issues identified:**')
    body.push('')
    for (const b of d.topBugs) {
      body.push(`- ${SEVERITY_EMOJI[b.severity] ?? ''} **${cap(b.severity)}** — ${b.title}`)
    }
  }
  body.push('')
  body.push(`Full interactive report: ${d.url}`)
  const bodyText = body.join('\n')
  return {
    text: `${title}\n\n${bodyText}`,
    intentUrl: `https://www.reddit.com/submit?title=${encodeURIComponent(title)}&text=${encodeURIComponent(bodyText)}`,
    intentLabel: 'Open in Reddit',
  }
}

// Each platform carries the UTM source/medium for its share action (see buildShareUrl). The URL
// embedded in the content is tagged for whichever platform is active, so every share button emits
// its own params. Adding a platform (e.g. Slack) is a single line here.
const PLATFORMS = [
  { key: 'overview', label: 'Overview', build: buildOverview, source: 'copy_link', medium: 'referral' },
  { key: 'x', label: 'X', build: buildX, source: 'twitter', medium: 'social' },
  { key: 'linkedin', label: 'LinkedIn', build: buildLinkedIn, source: 'linkedin', medium: 'social' },
  { key: 'reddit', label: 'Reddit', build: buildReddit, source: 'reddit', medium: 'social' },
] as const

type PlatformKey = typeof PLATFORMS[number]['key']

export default function ShareDialog({ project, onClose, readOnly, inline = false }: ShareDialogProps) {
  const [updateProject] = useUpdateProjectMutation()
  const [platform, setPlatform] = useState<PlatformKey>('overview')
  const [copied, setCopied] = useState(false)
  const [makingPublic, setMakingPublic] = useState(false)
  // Whether the "Learn more" reimbursement instructions are expanded in the CTA container.
  const [showReimburseSteps, setShowReimburseSteps] = useState(false)
  // The social preview card URL. Seeded from the project's cached card, then refreshed when the
  // dialog opens (and after "Make public") so it reflects the latest QA results.
  const [cardUrl, setCardUrl] = useState<string | null>(project.og_image_url ?? null)
  // The user-editable text shown in the preview. Seeded from the generated content and
  // reset whenever that content changes (e.g. switching platforms).
  const [editedText, setEditedText] = useState('')

  const bugCountsQuery = useGetBugCountsQuery(project.id)
  const overviewBugsQuery = useGetProjectBugsQuery({ projectId: project.id, page: 1, pageSize: 100, hideResolved: true, severitySort: 'desc' })
  const totalCount = bugCountsQuery.data?.total ?? 0
  const overviewItems = overviewBugsQuery.data?.items ?? []
  const openCount = bugCountsQuery.data?.openCount ?? 0
  const resolvedCount = bugCountsQuery.data?.resolvedCount ?? 0
  // The counts are what the summary is ABOUT, so with them missing the generated text reads "No bugs
  // found yet — looking clean so far. 🎉" and then rewrites itself once they arrive. Worse, a quick user
  // could copy that and post it. Usually these are already cached from the overview, so this rarely shows.
  const summaryPending = bugCountsQuery.isLoading || overviewBugsQuery.isLoading

  useEffect(() => {
    // Inline (panel) mode isn't a modal — Escape shouldn't close it, and there's no overlay/close.
    if (inline) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, inline])

  // Reset the copied indicator whenever the active platform changes.
  useEffect(() => { setCopied(false) }, [platform])

  // Regenerate the share card when the dialog opens so the preview (and the og:image a crawler
  // will later fetch) reflects the current results. Only members can trigger it; read-only viewers
  // keep the already-cached card. ?v= busts the browser cache when the image is rebuilt.
  const refreshCard = useCallback(async () => {
    if (readOnly) return
    try {
      const data = await updateProject({ id: project.id, data: { action: 'refresh-share-image' } }).unwrap()
      if (data.og_image_url) setCardUrl(`${data.og_image_url}?v=${data.og_image_version ?? 0}`)
    } catch { /* keep the existing cached card */ }
  }, [project.id, readOnly, updateProject])

  useEffect(() => { void refreshCard() }, [refreshCard])

  const data: ShareData = useMemo(() => {
    const topBugs = [...overviewItems]
      .sort((a: Bug, b: Bug) => (SEVERITY_RANK[a.severity] ?? 99) - (SEVERITY_RANK[b.severity] ?? 99))
      .slice(0, 5)
      .map((b: Bug) => ({ title: b.title, severity: b.severity }))
    return {
      projectName: project.name,
      url: `${window.location.origin}/projects/${project.id}/overview`,
      total: totalCount,
      topBugs,
    }
  }, [overviewItems, project.name, project.id, totalCount])

  const content = useMemo(() => {
    const def = PLATFORMS.find(p => p.key === platform) ?? PLATFORMS[0]
    // Tag the link with this action's own UTM params at the moment the platform is active, so each
    // share button (Copy = copy_link/referral, X/LinkedIn/Reddit = <platform>/social) is distinct.
    const taggedUrl = buildShareUrl(data.url, def.source, def.medium)
    return def.build({ ...data, url: taggedUrl })
  }, [platform, data])

  // Re-seed the editable text whenever the generated content changes (platform switch, data refresh).
  useEffect(() => { setEditedText(content.text) }, [content.text])

  const overLimit = content.charLimit != null && editedText.length > content.charLimit

  const handleCopy = () => {
    // The "Overview" tab is the plain copy-link action; the rest are per-platform shares. Collapse
    // "overview" to "other" so the property is exactly reddit/x/linkedin/other.
    trackSegmentEvent('Shared Qa project', { platform: platform === 'overview' ? 'other' : platform })
    void navigator.clipboard?.writeText(editedText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleMakePublic = async () => {
    setMakingPublic(true)
    try {
      await updateProject({
        id: project.id,
        data: { action: 'update-settings', is_public: true },
      }).unwrap()
      // Now public — build/refresh the card so the preview appears without reopening the dialog.
      void refreshCard()
    } catch {
      // Leave the note in place; the toggle in Settings remains available as a fallback.
    } finally {
      setMakingPublic(false)
    }
  }

  const body = (
    <>
        <div className="share-dialog__reimburse" data-testid="share-dialog-reimburse">
          <Gift size={18} aria-hidden="true" />
          <div className="share-dialog__reimburse-body">
            <span>
              <strong>Get reimbursed for this project’s credits.</strong> Share your results to your
              socials and we’ll reimburse the credits this project consumed.
            </span>
            <button
              type="button"
              className="share-dialog__reimburse-toggle"
              onClick={() => setShowReimburseSteps(v => !v)}
              aria-expanded={showReimburseSteps}
              aria-controls="share-dialog-reimburse-steps"
              data-testid="share-dialog-reimburse-toggle"
              data-analytics-event="share-reimburse-learn-more"
            >
              Learn more
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`share-dialog__reimburse-chevron${showReimburseSteps ? ' share-dialog__reimburse-chevron--open' : ''}`}
              />
            </button>
            {showReimburseSteps && (
              <ol
                id="share-dialog-reimburse-steps"
                className="share-dialog__reimburse-steps"
                data-testid="share-dialog-reimburse-steps"
              >
                <li>Make sure to set the project as public.</li>
                <li>Copy your project URL below.</li>
                <li>Share it to a social channel (X, Reddit, Hacker News, etc.).</li>
                <li>Paste the social thread URL into project settings.</li>
                <li>Automatic reimbursement will process.</li>
              </ol>
            )}
          </div>
        </div>

        <div className="share-dialog__card-preview" data-testid="share-dialog-card-preview">
          {cardUrl ? (
            <img
              className="share-dialog__card-img"
              src={cardUrl}
              alt={`Social preview card for ${project.name}`}
              width={1200}
              height={630}
              loading="lazy"
            />
          ) : (
            <div className="share-dialog__card-placeholder" data-testid="share-dialog-card-placeholder">
              Social preview card is generating…
            </div>
          )}
        </div>

        <div className="share-dialog__stats" data-testid="share-dialog-stats">
          <div className="share-dialog__stat">
            <span className="share-dialog__stat-value">{totalCount}</span>
            <span className="share-dialog__stat-label">Total bugs</span>
          </div>
          <div className="share-dialog__stat">
            <span className="share-dialog__stat-value">{openCount}</span>
            <span className="share-dialog__stat-label">Open</span>
          </div>
          <div className="share-dialog__stat">
            <span className="share-dialog__stat-value">{resolvedCount}</span>
            <span className="share-dialog__stat-label">Resolved</span>
          </div>
        </div>

        <div className="share-dialog__tabs" role="tablist" aria-label="Platform">
          {PLATFORMS.map(p => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={platform === p.key}
              className={`share-dialog__tab${platform === p.key ? ' share-dialog__tab--active' : ''}`}
              onClick={() => setPlatform(p.key)}
              data-testid={`share-tab-${p.key}`}
              data-analytics-event={`share-tab-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {summaryPending ? (
          <div
            className="share-dialog__preview share-dialog__preview--loading"
            role="status"
            aria-live="polite"
            data-testid="share-dialog-preview-loading"
          >
            <Spinner size={18} /> Preparing your summary…
          </div>
        ) : (
          <textarea
            className="share-dialog__preview"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            rows={12}
            aria-label="Shareable content"
            data-testid="share-dialog-preview"
            data-analytics-event="share-content-input"
          />
        )}

        {/* Bottom section is sticky so the "Make public" text and Copy button stay reachable even
            when the modal is taller than the viewport. Text sits on the left, actions on the right. */}
        <div className="share-dialog__footer" data-testid="share-dialog-footer">
          <div className="share-dialog__meta">
            {content.charLimit != null && (
              <span className={`share-dialog__count${overLimit ? ' share-dialog__count--over' : ''}`} data-testid="share-char-count">
                {editedText.length} / {content.charLimit}
              </span>
            )}
            {!project.is_public && (
              readOnly ? (
                <span className="share-dialog__note" data-testid="share-private-note">
                  This project is private — ask an owner to make it public so anyone can open this link.
                </span>
              ) : (
                <button
                  type="button"
                  className="share-dialog__make-public"
                  onClick={handleMakePublic}
                  disabled={makingPublic}
                  data-testid="share-make-public"
                  data-analytics-event="share-make-public-button"
                >
                  <Globe size={13} aria-hidden="true" />
                  {makingPublic ? 'Making public…' : 'Make public to share this link'}
                </button>
              )
            )}
          </div>

          <div className="share-dialog__actions">
            {content.intentUrl && (
              <a
                className="share-dialog__open-btn"
                href={content.intentUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-open-btn"
                data-analytics-event="share-open-link"
              >
                <ExternalLink size={14} aria-hidden="true" />
                {content.intentLabel}
              </a>
            )}
            <button
              type="button"
              className="share-dialog__copy-btn"
              onClick={handleCopy}
              disabled={!project.is_public || summaryPending}
              title={!project.is_public ? 'Make the project public to copy a shareable link' : undefined}
              data-testid="share-copy-btn"
              data-analytics-event="share-copy"
            >
              {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
    </>
  )

  // Inline: render in the app's left panel (rail → Share) with just a title header — no overlay,
  // backdrop, or close button. Switching panels via the rail is how you leave it.
  if (inline) {
    return (
      <div className="share-dialog share-dialog--inline" data-testid="share-dialog">
        <div className="share-dialog__content share-dialog__content--inline">
          <div className="share-dialog__header">
            <h2 className="share-dialog__title">Share QA Results</h2>
          </div>
          {body}
        </div>
      </div>
    )
  }

  return (
    <ModalPortal>
    <div className="share-dialog__overlay" data-testid="share-dialog" onClick={onClose} data-analytics-event="share-overlay-close">
      <div
        className="share-dialog__content"
        role="dialog"
        aria-labelledby="share-dialog-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="share-dialog__header">
          <h2 id="share-dialog-title" className="share-dialog__title">Share QA Results</h2>
          <button
            type="button"
            className="share-dialog__close"
            onClick={onClose}
            aria-label="Close"
            data-testid="share-dialog-close"
            data-analytics-event="share-close-button"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {body}
      </div>
    </div>
    </ModalPortal>
  )
}
